import type { City, Place } from '../world/city';
import type { PhysicsCity } from '../physics/world';
import type { Person, Population } from './population';
import type { WalkNode } from './walking';
import type { Vec } from '../destruction/structure';

interface Refuge {
  person: Person;
  state: 'seeking' | 'travelling' | 'sheltered' | 'waiting for transport' | 'blocked' | 'incapacitated';
  place?: Place;
  token: string;
  retry: number;
  failed: Set<string>;
  attempts: number;
  reason: string;
  accessVersion?: number;
}
/** Indoor refuge is a reserved, supported destination of an original resident.
 * Existing transit completes its safe handoff; there is no mass relocation. */
export class Shelters {
  active = false;
  event = 0;
  time = 0;
  records = new Map<string, Refuge>();
  private epoch = 0;
  private cursor = 0;
  private age = 0;
  private slots = new Map<string, WalkNode[]>();
  private builder?: Generator<void>;
  private nextSerial = 0;
  constructor(
    readonly city: City,
    readonly physics: PhysicsCity,
    readonly population: Population,
  ) {
    this.reset();
  }
  reset(): void {
    this.clear();
    this.epoch = this.physics.life.epoch;
    this.builder = this.prepare();
  }
  clear(): void {
    this.cancel();
    this.records.clear();
    this.slots.clear();
    this.builder = undefined;
    this.time = this.cursor = this.age = this.nextSerial = 0;
  }
  private *prepare(): Generator<void> {
    const network = this.population.civil?.network;
    if (!network) return;
    const privateBuildings = new Set(this.city.strategic?.parcels.flatMap((p) => p.buildings) ?? []);
    let count = 0;
    for (const node of network.nodes) {
      if (++count % 256 === 0) yield;
      if (
        node.restricted ||
        !node.layer.startsWith('interior:') ||
        !node.supportCell ||
        privateBuildings.has(node.buildingId!) ||
        Number(node.layer.split(':')[2]) > 1
      )
        continue;
      // Spacing follows the real floor grid. Clearance is checked when a slot
      // is selected, so changed furniture and damaged rooms stay authoritative.
      const key = `${Math.floor(node.x / 32)}:${Math.floor(node.z / 32)}`;
      const list = this.slots.get(key) ?? [];
      list.push(node);
      this.slots.set(key, list);
    }
  }
  start(): void {
    if (this.active) return;
    this.active = true;
    this.event++;
    this.records.clear();
    this.cursor = this.age = 0;
  }
  cancel(): void {
    this.active = false;
    this.event++;
    for (const r of this.records.values()) {
      const civil = this.population.civil;
      if (civil?.task(r.person)?.token !== r.token) continue;
      civil.cancelTask(r.person, 'Shelter alert ended; original routine can resume');
      if (!r.person.fatal && !['incapacitated', 'falling', 'carried', 'recovering'].includes(r.person.state))
        civil.resume(r.person, r.person.layer ?? 'street');
    }
  }
  private safe(place: Place): boolean {
    return !!this.population.civil?.network.available(place) && !this.population.hazardAt(place);
  }
  private candidates(person: Person, record: Refuge): Place[] {
    const civil = this.population.civil!,
      home = civil.routines.get(person.id)?.home,
      result: Place[] = [];
    if (home && !home.access && this.safe(home) && !record.failed.has(home.id)) result.push(home);
    const cx = Math.floor(person.p.x / 32),
      cz = Math.floor(person.p.z / 32),
      range = Math.min(18, 3 + Math.floor(record.attempts / 3) * 3),
      candidates: WalkNode[] = [];
    for (let x = cx - range; x <= cx + range; x++)
      for (let z = cz - range; z <= cz + range; z++)
        for (const n of this.slots.get(`${x}:${z}`) ?? []) candidates.push(n);
    candidates.sort((a, b) => this.distance(a, person.p) - this.distance(b, person.p));
    for (const node of candidates) {
      const id = `mission-node:${node.id}`;
      if (record.failed.has(id) || civil.reservations.holders(id, this.time).length) continue;
      const place = civil.nodePlace(node);
      if (!this.safe(place) || this.physics.blocked(node.x, node.z, 0.3, node.y + 1)) continue;
      result.push(place);
      if (result.length >= 5) break;
    }
    return result;
  }
  private distance(a: Vec, b: Vec): number {
    return (a.x - b.x) ** 2 + (a.z - b.z) ** 2 + (a.y - b.y) ** 2 * 4;
  }
  private step(person: Person): void {
    const civil = this.population.civil!;
    let r = this.records.get(person.id);
    if (!r) {
      r = {
        person,
        state: 'seeking',
        token: '',
        retry: 0,
        attempts: 0,
        failed: new Set(),
        reason: 'Finding a supported indoor refuge',
      };
      this.records.set(person.id, r);
    }
    if (person.fatal || ['incapacitated', 'falling', 'carried', 'recovering'].includes(person.state)) {
      r.state = 'incapacitated';
      if (civil.task(person)?.token === r.token) civil.cancelTask(person, 'Refuge occupant needs assistance');
      return;
    }
    if (person.hidden || person.carrier || person.controlled) {
      r.state = 'waiting for transport';
      r.reason = 'Original passenger or driver must reach a safe disembarkation point';
      return;
    }
    const task = civil.task(person);
    if (r.accessVersion !== undefined && r.accessVersion !== this.physics.footGeometryVersion(person.p)) {
      r.accessVersion = undefined;
      r.retry = this.time;
    }
    if (task?.token === r.token && task.state === 'failed' && task.blockedAtOrigin) {
      // Changing the remote destination cannot release a resident enclosed by
      // local rubble. Retain the original person and retry on local changes,
      // with bounded backoff while the same obstruction remains.
      r.place = undefined;
      r.token = '';
      r.state = 'blocked';
      r.reason = 'No supported exit from this position; waiting for access or assistance';
      r.attempts++;
      r.accessVersion = this.physics.footGeometryVersion(person.p);
      r.retry = this.time + Math.min(12, 2 ** Math.min(4, r.attempts));
      return;
    }
    if (task?.token === r.token && task.state === 'active' && r.place && this.safe(r.place)) {
      r.state = person.state === 'sheltering' ? 'sheltered' : 'travelling';
      r.reason = task.reason;
      return;
    }
    if (r.place && (task?.state === 'failed' || !this.safe(r.place))) {
      r.failed.add(r.place.id);
      r.attempts++;
      if (task?.token === r.token) civil.cancelTask(person, 'Refuge or its access became unsafe');
      r.place = undefined;
      r.token = '';
      r.retry = this.time + 1;
    }
    if (this.time < r.retry || !civil.prepareForTask(person)) return;
    // Queueing tickets are cancelled only after any real aboard journey ends.
    this.population.rail?.cancel(person.id, 'Shelter alert interrupts the waiting commute');
    this.population.buses?.cancel(person.id, 'Shelter alert interrupts the waiting commute');
    for (const place of this.candidates(person, r)) {
      const token = `shelter:${this.event}:${person.id}:${++this.nextSerial}`;
      if (civil.assignTask(person, place, token, 'sheltering', Infinity)) {
        r.place = place;
        r.token = token;
        r.state = 'travelling';
        r.reason = 'Walking through reserved doors to a real indoor refuge';
        return;
      }
    }
    r.attempts++;
    r.retry = this.time + 4;
    r.state = 'blocked';
    r.reason = 'No reachable unoccupied safe indoor slot yet; retaining the original person';
  }
  update(dt: number): void {
    if (this.epoch !== this.physics.life.epoch) return;
    this.time += dt;
    if (this.builder?.next().done) this.builder = undefined;
    if (!this.active || !this.population.civil) return;
    this.age += dt;
    if (this.age < 0.1) return;
    this.age %= 0.1;
    const people = this.population.people;
    for (let i = 0; i < Math.min(32, people.length); i++) {
      const person = people[this.cursor++ % people.length];
      if (person.id.startsWith('person-')) this.step(person);
    }
  }
  snapshot(): Record<string, number> {
    const list = [...this.records.values()];
    return {
      shelterAlert: Number(this.active),
      shelterResidents: list.length,
      shelterOccupants: list.filter((r) => r.state === 'sheltered').length,
      shelterTravelling: list.filter((r) => r.state === 'travelling').length,
      shelterBlocked: list.filter((r) => r.state === 'blocked').length,
      shelterWaitingTransport: list.filter((r) => r.state === 'waiting for transport').length,
    };
  }
  inspect(): unknown {
    return [...this.records.values()].map((r) => ({
      person: r.person.id,
      state: r.state,
      place: r.place?.id,
      position: r.person.p.toArray(),
      attempts: r.attempts,
      reason: r.reason,
    }));
  }
}
